import Loader from "@/ui/atoms/Loader";
import AnchorUnderlined from "@/ui/atoms/AnchorUnderlined";

import MessageWithButton from "@/ui/molecules/MessageWithButton";

import LayoutFillParentOverlay from "@/ui/layouts/LayoutFillParentOverlay";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import { useDatabaseAccessRevoked } from "@/store/databaseAccessRevokedStore";

export type ViewDatabaseGuardParams = {
    useState: () => {
        databaseLoadStatus: "idle" | "pending" | "resolved" | "rejected";
        initialLoadingSuccess: boolean;
        databasesCount: number | null;
        selectedDatabaseIds: number[] | null;
    };
};

export type ViewDatabaseGuardProps = {
    children: React.ReactNode;
    loader?: React.ReactNode;
};

export const viewDatabaseGuardOverlayMessages = {
    loadingDatabases: { message: "Loading databases..." },
    failedToLoadDatabases: {
        message: "Failed to load databases, please try again or",
    },
    userDoNotHaveAnyDatabases: { message: "No databases assigned," },
    selectDatabaseBeforeProceeding: {
        header: "No databases selected.",
        message: "To proceed, please select a database above.",
    },
    databaseAccessRevoked: {
        header: "Database access revoked",
        message:
            "You no longer have access to this database. Please contact your administrator if you believe this is an error.",
    },
} satisfies Record<
    string,
    {
        header?: string;
        message: string;
    }
>;

export const testIds = {
    overlay: "ViewDatabaseGuardOverlay",
};
export const createViewDatabaseGuard = ({
    useState,
}: ViewDatabaseGuardParams) => {
    const ViewDatabaseGuard = (props: ViewDatabaseGuardProps) => {
        const { children, loader } = props;

        const {
            databaseLoadStatus,
            initialLoadingSuccess,
            databasesCount,
            selectedDatabaseIds,
        } = useState();

        const databaseAccessRevoked = useDatabaseAccessRevoked();

        const noDatabases = databasesCount === 0;
        const databaseNotSelected = selectedDatabaseIds === null;
        const databasesLoadError = databaseLoadStatus === "rejected";
        const databasesLoading = databaseLoadStatus === "pending";

        const displayLoader = databasesLoading && !initialLoadingSuccess;
        const displayPromptToSelectDatabase =
            initialLoadingSuccess && !noDatabases && databaseNotSelected;
        const userDoesNotHaveAnyDatabases =
            initialLoadingSuccess && noDatabases;

        const displayOverlay =
            displayLoader ||
            displayPromptToSelectDatabase ||
            userDoesNotHaveAnyDatabases ||
            databasesLoadError ||
            databaseAccessRevoked;

        if (!displayOverlay) {
            return children;
        }

        return (
            <>
                {displayOverlay && (
                    <LayoutFillParentOverlay
                        data-testid={testIds.overlay}
                        className={testIds.overlay}
                        sx={{
                            // @HACK this make sure that database dropdown is on top of overlay (it has z-index: 3)
                            zIndex: 2,
                        }}
                    >
                        <LayoutFillParentCentered>
                            {!displayLoader && databasesLoadError && (
                                <MessageWithButton
                                    variant="error"
                                    message={
                                        <>
                                            {
                                                viewDatabaseGuardOverlayMessages
                                                    .failedToLoadDatabases
                                                    .message
                                            }{" "}
                                            <AnchorUnderlined
                                                href={"mailto:support@proda.ai"}
                                            >
                                                contact support
                                            </AnchorUnderlined>
                                        </>
                                    }
                                />
                            )}
                            {!displayLoader && userDoesNotHaveAnyDatabases && (
                                <MessageWithButton
                                    variant="error"
                                    message={
                                        <>
                                            {
                                                viewDatabaseGuardOverlayMessages
                                                    .userDoNotHaveAnyDatabases
                                                    .message
                                            }{" "}
                                            <AnchorUnderlined
                                                href={"mailto:support@proda.ai"}
                                            >
                                                contact support
                                            </AnchorUnderlined>
                                        </>
                                    }
                                />
                            )}
                            {!displayLoader &&
                                displayPromptToSelectDatabase && (
                                    <MessageWithButton
                                        variant="info"
                                        header={
                                            viewDatabaseGuardOverlayMessages
                                                .selectDatabaseBeforeProceeding
                                                .header
                                        }
                                        message={
                                            viewDatabaseGuardOverlayMessages
                                                .selectDatabaseBeforeProceeding
                                                .message
                                        }
                                    />
                                )}
                            {databaseAccessRevoked && (
                                <MessageWithButton
                                    variant="error"
                                    header={
                                        viewDatabaseGuardOverlayMessages
                                            .databaseAccessRevoked.header
                                    }
                                    message={
                                        viewDatabaseGuardOverlayMessages
                                            .databaseAccessRevoked.message
                                    }
                                />
                            )}
                            {displayLoader &&
                                (loader !== undefined ? (
                                    loader
                                ) : (
                                    <Loader
                                        label={
                                            viewDatabaseGuardOverlayMessages
                                                .loadingDatabases.message
                                        }
                                    />
                                ))}
                        </LayoutFillParentCentered>
                    </LayoutFillParentOverlay>
                )}
            </>
        );
    };
    return ViewDatabaseGuard;
};
